import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Bot, Building2, CreditCard, FileText, Gauge, Menu, Plug, Settings, User, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

const TABS = [
  { to: '/einstellungen/profil', label: 'Profil', icon: User },
  { to: '/einstellungen/team', label: 'Team', icon: Users },
  { to: '/einstellungen/usage', label: 'Nutzung', icon: Gauge },
  { to: '/einstellungen/billing', label: 'Abrechnung', icon: CreditCard },
  { to: '/einstellungen/brand', label: 'Brand-Profile', icon: Building2 },
  { to: '/einstellungen/context', label: 'Kontext-Dokumente', icon: FileText },
  { to: '/einstellungen/connectors', label: 'Konnektoren', icon: Plug },
  { to: '/einstellungen/verbundene-apps', label: 'Verbundene Apps', icon: Bot },
] as const

function SettingsNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Settings className="h-4 w-4 shrink-0" />
        <h2 className="text-[16px] font-[650] tracking-tight">Einstellungen</h2>
      </div>
      <nav className="space-y-1 p-2">
        {TABS.map((tab) => {
          const Icon = tab.icon
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 min-[761px]:min-h-9 items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
                    : 'text-muted-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="min-w-0 break-words">{tab.label}</span>
            </NavLink>
          )
        })}
      </nav>
    </>
  )
}

// R1: Der 761px-Umschaltpunkt der globalen App-Navigation (Drawer bis 760px,
// feste Sidebar ab 761px). Bei 761px bleiben nur ~533px Inhalt — die zweite
// Navigation darf hier nicht blind als weitere feste Spalte einblenden.
const SETTINGS_DESKTOP_QUERY = '(min-width: 1180px)'

export function SettingsLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [desktopNav, setDesktopNav] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(SETTINGS_DESKTOP_QUERY).matches : false,
  )
  // R1: echter Toggle-Button als Fokusziel nach dem Schliessen des Sheets.
  const toggleRef = useRef<HTMLButtonElement>(null)
  // R1: beständiger Hauptbereich als Fallback-Fokusziel (z. B. nach Resize
  // auf die Desktop-Ansicht, wenn der Toggle nicht mehr sichtbar ist).
  const mainRef = useRef<HTMLElement>(null)

  // R1: Wechsel auf die breite Desktop-Ansicht schliesst das mobile Sheet und
  // gibt den Fokus an den (dann sichtbaren) beständigen Seitenbereich zurück.
  useEffect(() => {
    const media = window.matchMedia(SETTINGS_DESKTOP_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      setDesktopNav(event.matches)
      if (event.matches) setMobileNavOpen(false)
    }
    media.addEventListener('change', handleChange)
    return () => media.removeEventListener('change', handleChange)
  }, [])

  return (
    <div className="flex h-full">
      {/* Desktop sidebar — erst ab 1180px, damit bei 761px die globale
          App-Navigation (228px) plus diese Spalte den Inhalt nicht verdraengt */}
      <aside
        className={cn(
          'hidden w-60 shrink-0 border-r border-border bg-muted/20 min-[1180px]:flex min-[1180px]:flex-col',
          desktopNav && 'flex flex-col',
        )}
      >
        <SettingsNav />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile Nav-Bar (M8.4) — spiegelt das App-Shell-Pattern */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 min-[1180px]:hidden">
          <Sheet
            open={mobileNavOpen}
            onOpenChange={(open) => {
              setMobileNavOpen(open)
            }}
          >
            {/* R1: SheetTrigger asChild — Radix merkt sich den echten Auslöser
                und kann nach Escape den Fokus selbst zurückgeben. Genau eine
                Sheet-Instanz, kein zweiter Root. */}
            <SheetTrigger asChild>
              <button
                ref={toggleRef}
                type="button"
                className="-ml-1 inline-flex h-11 w-11 min-[761px]:h-9 min-[761px]:w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Einstellungen-Navigation öffnen"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-60 max-w-[85vw] p-0"
              onCloseAutoFocus={(event) => {
                // R1: Fokus landet auf dem echten Toggle, wenn er verbunden und
                // sichtbar ist — sonst auf dem beständigen Hauptbereich.
                const toggle = toggleRef.current
                if (toggle && toggle.isConnected && toggle.getClientRects().length > 0) {
                  event.preventDefault()
                  toggle.focus()
                  return
                }
                const main = mainRef.current
                if (main && main.isConnected && main.getClientRects().length > 0) {
                  event.preventDefault()
                  main.focus()
                }
              }}
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Einstellungen-Navigation</SheetTitle>
              </SheetHeader>
              <SettingsNav onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="text-sm font-semibold">Einstellungen</span>
        </div>
        <main ref={mainRef} tabIndex={-1} aria-label="Einstellungen" className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
